<template>
  <section class="ui-page">
    <AppFilterToolbar>
      <AppDatePicker
        v-model="filter_dates"
        design="new"
        range
        value-type="YYYY-MM-DD"
        format="DD MMM YYYY"
        :shortcuts="dateShortcuts"
        :input-attr="{ id: 'trengo-webhook-dates', 'aria-label': 'Date range' }"
        placeholder="Date range"
        @input="onDateInput"
      />

      <AppSelect
        v-model="country_filter"
        design="new"
        input-id="trengo-webhook-country"
        :reduce="item => item.code"
        :options="country_options"
        placeholder="Country"
        aria-label="Country"
        @input="onFilterChange"
      />

      <AppSelect
        v-model="adv_source_filter"
        design="new"
        input-id="trengo-webhook-source"
        :options="advertising_source_options"
        placeholder="Advertising source"
        aria-label="Advertising source"
        @input="onInputAdvertisingSourceFilter"
      />

      <AppSelect
        v-model="adv_campaign_filter"
        design="new"
        input-id="trengo-webhook-campaign"
        :options="adv_campaign_options_filter"
        :reduce="item => item.id"
        :disabled="!adv_campaign_options_filter.length"
        placeholder="Advertising campaign"
        aria-label="Advertising campaign"
        @input="onFilterChange"
      />
      <template v-if="hasActiveFilters" #clear>
        <AppClearFiltersButton design="new" icon-only @clear="clearFilters" />
      </template>
    </AppFilterToolbar>

    <AppContentSkeleton v-if="loading" label="Loading WhatsApp webhooks" :columns="columns" min-width="1200px" />
    <AppErrorState v-else-if="error" :message="error" @retry="fetchItems" />

    <AppCard v-else-if="items.length" design="new" variant="table" header-density="compact">
      <AppDataTable
        design="new"
        density="dense"
        :rows="items"
        :columns="columns"
        :loading="loading"
      >
        <template #cell-created_at="{ row }">
          <AppTableText :text="dateTimeFormat(row.created_at)" />
        </template>

        <template #cell-language="{ row }">
          <AppTableText :text="row.language && row.language.name" />
        </template>

        <template #cell-country="{ row }">
          <div v-if="row.country" class="ui-cell-inline">
            <span :class="`fflag ff-sm fflag-${row.country.code || 'empty'}`" aria-hidden="true" />
            <AppTableText :text="row.country.name" />
          </div>
          <span v-else>—</span>
        </template>

        <template #cell-visitor="{ row }">
          <AppTableText :text="row.visitor" />
        </template>

        <template #cell-source="{ row }">
          <AppTableText :text="row.adv_source && row.adv_source.name" />
        </template>

        <template #cell-campaign="{ row }">
          <AppTableText :text="row.adv_campaign && row.adv_campaign.name" />
        </template>

        <template #cell-ticket="{ row }">
          <AppTableText v-if="row.trengo_ticket" :text="row.trengo_ticket.ticket_id ? `#${row.trengo_ticket.ticket_id}` : null">
            <RouterLink :to="{ name: 'trengo-tickets-detail', params: { id: row.id } }" class="ui-link">#{{ row.trengo_ticket.ticket_id }}</RouterLink>
          </AppTableText>
          <span v-else>—</span>
        </template>
      </AppDataTable>
    </AppCard>
    <AppNoResults
      v-else
      design="new"
      title="No WhatsApp webhooks found"
      text="Try changing the date range or filters."
    />
    <AppPagination
      v-if="!loading && !error && items.length"
      design="new"
      placement="embedded"
      :page="pagination.page"
      :pages="pagination.pages"
      :count="pagination.count"
      :visible-count="items.length"
      :page-size="PAGE_SIZE"
      :loading="loading"
      label="WhatsApp webhook pages"
      @change="onClickPagination"
    />
  </section>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { getCountries, getLeadsAdvSources, getLeadsTrengoWAWebhooks } from '@/http'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import AppErrorState from '@/components/AppErrorState.vue'
import AppTableText from '@/components/AppTableText.vue'

const PAGE_SIZE = 40

function recentRange(days) {
  const end = new Date()
  const start = new Date(end)
  start.setDate(start.getDate() - days)
  return [start, end]
}

function currentYearRange(offset = 0) {
  const now = new Date()
  const year = now.getFullYear() + offset
  const start = new Date(year, 0, 1)
  const end = offset ? new Date(year, 11, 31) : now
  return [start, end]
}

const DATE_SHORTCUTS = [
  { text: 'Last 7 days', onClick: () => recentRange(7) },
  { text: 'Last 30 days', onClick: () => recentRange(30) },
  { text: 'Last 60 days', onClick: () => recentRange(60) },
  { text: 'Last 90 days', onClick: () => recentRange(90) },
  { text: 'Last 180 days', onClick: () => recentRange(180) },
  { text: 'This year', onClick: () => currentYearRange() },
  { text: 'Last year', onClick: () => currentYearRange(-1) }
]

export default {
  name: 'TrengoWAWebhooks',
  components: { AppContentSkeleton, AppErrorState, AppTableText },
  data() {
    return {
      PAGE_SIZE,
      items: [],
      loading: false,
      error: '',
      adv_source_filter: null,
      advertising_source_options: [],
      adv_campaign_filter: null,
      adv_campaign_options_filter: [],
      country_filter: null,
      country_options: [],
      filter_dates: null,
      dateShortcuts: DATE_SHORTCUTS,
      pagination: {
        pages: 1,
        page: 1,
        count: 0
      },
      requestId: 0,
      columns: [
        { key: 'created_at', label: 'Date', width: '15%' },
        { key: 'language', label: 'Language', width: '12%' },
        { key: 'country', label: 'Country', width: '14%' },
        { key: 'visitor', label: 'Visitor', width: '20%' },
        { key: 'source', label: 'Source', width: '14%' },
        { key: 'campaign', label: 'Campaign', width: '14%' },
        { key: 'ticket', label: 'Trengo ticket', width: '11%' }
      ]
    }
  },
  computed: {
    hasActiveFilters() {
      return Boolean(
        this.filter_dates
        || this.country_filter
        || this.adv_source_filter
        || this.adv_campaign_filter
      )
    }
  },
  created() {
    this.fetchItems()
    this.loadFilterOptions()
  },
  beforeDestroy() {
    this.requestId += 1
  },
  methods: {
    async fetchItems() {
      const requestId = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const response = await getLeadsTrengoWAWebhooks({
          page: this.pagination.page,
          page_size: PAGE_SIZE,
          date_from: this.filter_dates && this.filter_dates[0],
          date_to: this.filter_dates && this.filter_dates[1],
          adv_source: this.adv_source_filter && this.adv_source_filter.code,
          adv_campaign: this.adv_campaign_filter,
          country: this.country_filter
        })
        if (requestId !== this.requestId) return
        this.items = response.data.results
        this.pagination.pages = response.data.pages
        this.pagination.count = response.data.count
      } catch (error) {
        if (requestId === this.requestId) this.error = this.errorMessage(error, 'Unable to load WhatsApp webhooks.')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async loadFilterOptions() {
      try {
        const [sources, countries] = await Promise.all([
          getLeadsAdvSources(),
          getCountries()
        ])
        this.advertising_source_options = sources.data
        this.country_options = countries.data
      } catch (error) {
        this.$notify({ type: 'error', title: this.errorMessage(error, 'Unable to load filter options.') })
      }
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onFilterChange() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onDateInput(value) {
      if (!value || value.length === 2) this.onFilterChange()
    },
    onInputAdvertisingSourceFilter(source) {
      this.adv_campaign_filter = null
      this.adv_campaign_options_filter = source ? source.campaigns : []
      this.onFilterChange()
    },
    clearFilters() {
      this.filter_dates = null
      this.country_filter = null
      this.adv_source_filter = null
      this.adv_campaign_filter = null
      this.adv_campaign_options_filter = []
      this.pagination.page = 1
      this.fetchItems()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy, HH:mm') : '—'
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      return (data && (data.detail || data.message)) || fallback
    }
  }
}
</script>
